/**
 * Iter-328 Phase 1 — Native Staff Payments through Stripe Connect.
 *
 * Admin surface for:
 *   • Reviewing the live platform balance
 *   • Building a payment batch from a funded invoice
 *   • Approving the batch (with shortfall messaging)
 *   • Executing the batch (creates Stripe Transfers per talent)
 *   • Retry / Hold / Release individual lines
 *   • Initiating a Stripe Topup from the linked business bank account
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Wallet, DollarSign, Loader2, RefreshCw, CheckCircle2,
  AlertTriangle, Pause, Play, RotateCcw, PlusCircle, Users, Send, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import AdminStaffPaymentsLifecycle from '../../components/AdminStaffPaymentsLifecycle';
import CriticalPayQueueCard from '../../components/admin/CriticalPayQueueCard';

const STATUS_META = {
  awaiting_funding:       { text: 'Awaiting Funding',        cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  funding_received:       { text: 'Funding Received',        cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  payment_ready:          { text: 'Payment Ready',           cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  payment_approved:       { text: 'Payment Approved',        cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  stripe_funding_pending: { text: 'Stripe Funding Pending',  cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  transferred:            { text: 'Transferred',             cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  payout_processing:      { text: 'Payout Processing',       cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  paid:                   { text: 'Paid',                    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed:                 { text: 'Failed',                  cls: 'bg-red-100 text-red-700 border-red-200' },
  returned:               { text: 'Returned',                cls: 'bg-red-100 text-red-700 border-red-200' },
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminStaffPayments() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState({ available_usd: 0, pending_usd: 0, configured: false });
  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [approveResult, setApproveResult] = useState(null);

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupDesc, setTopupDesc] = useState('iEvntür contractor payout funding');
  const [topupBusy, setTopupBusy] = useState(false);
  const [mandateNeeded, setMandateNeeded] = useState(false);
  const [mandateBusy, setMandateBusy] = useState(false);
  const [mandateAction, setMandateAction] = useState(null);
  const [alertBuffer, setAlertBuffer] = useState('');
  const [bufferSaving, setBufferSaving] = useState(false);

  useEffect(() => {
    if (!topupOpen) return;
    api.get('/admin/stripe/funding-alert-config')
      .then((r) => setAlertBuffer(String(r.data.buffer ?? 0)))
      .catch(() => {});
  }, [topupOpen]);

  const saveBuffer = async () => {
    const val = parseFloat(alertBuffer);
    if (Number.isNaN(val) || val < 0) { toast.error('Enter a valid buffer amount'); return; }
    setBufferSaving(true);
    try {
      await api.put('/admin/stripe/funding-alert-config', { buffer: val });
      toast.success(`Low-balance alert buffer set to $${val.toFixed(2)}`, {
        description: 'The daily funding alert now fires when balance < upcoming payouts + buffer.',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to save buffer');
    } finally { setBufferSaving(false); }
  };

  // Iter-328 Phase 2 — Payroll blockers panel
  const [readiness, setReadiness] = useState({ funded_awaiting_batch: 0, batches_pending_approval: 0, talents_not_payout_ready: [] });
  const [reminderBusy, setReminderBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, bs, r] = await Promise.all([
        api.get('/admin/stripe/platform-balance'),
        api.get('/admin/staff-payments/batches' + (statusFilter ? `?status=${statusFilter}` : '')),
        api.get('/admin/staff-payments/readiness'),
      ]);
      setBalance(b.data);
      setBatches(bs.data || []);
      setReadiness(r.data || { funded_awaiting_batch: 0, batches_pending_approval: 0, talents_not_payout_ready: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setApproveResult(null);
    try {
      const res = await api.get(`/admin/staff-payments/batches/${id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const approve = async () => {
    setActionBusy(true);
    try {
      const res = await api.post(`/admin/staff-payments/batches/${detail.id}/approve`);
      setApproveResult(res.data);
      if (res.data.can_execute) toast.success('Approved — ready to execute');
      else toast.warning(res.data.message);
      await loadAll();
      await openDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setActionBusy(false); }
  };

  const execute = async () => {
    if (!window.confirm(`Send ${detail.lines.length} Stripe transfers now?`)) return;
    setActionBusy(true);
    try {
      const res = await api.post(`/admin/staff-payments/batches/${detail.id}/execute`);
      toast.success(`${res.data.transfers_created} transfers created, ${res.data.failures} failed`);
      await loadAll();
      await openDetail(detail.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === 'object' ? d.message || JSON.stringify(d) : (d || 'Execute failed');
      toast.error(msg);
    } finally { setActionBusy(false); }
  };

  const holdLine = async (payoutId) => {
    const reason = window.prompt('Hold reason (optional):', '');
    if (reason === null) return;
    try {
      await api.post(`/admin/staff-payments/lines/${payoutId}/hold`, { reason });
      toast.success('Line held');
      await openDetail(detail.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Hold failed'); }
  };
  const releaseLine = async (payoutId) => {
    try {
      await api.post(`/admin/staff-payments/lines/${payoutId}/release`);
      toast.success('Line released');
      await openDetail(detail.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Release failed'); }
  };
  const retryLine = async (payoutId) => {
    try {
      await api.post(`/admin/staff-payments/lines/${payoutId}/retry`);
      toast.success('Line reset — click Execute to send the transfer');
      await openDetail(detail.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  // Iter-328 Phase 3 — Stripe Instant Payout (requires talent debit card)
  const instantPayout = async (payoutId) => {
    if (!window.confirm('Send Instant Payout? (Extra Stripe fee ~1.5%, min $0.50)')) return;
    try {
      const res = await api.post(`/admin/staff-payments/lines/${payoutId}/instant-payout`);
      toast.success(`Instant payout sent — ${res.data.stripe_payout_id} · ${res.data.status}`);
      await openDetail(detail.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Instant payout failed'); }
  };

  const submitTopup = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setTopupBusy(true);
    try {
      const res = await api.post('/admin/stripe/topup', { amount: amt, description: topupDesc });
      toast.success(`Topup ${res.data.topup_id} initiated ($${amt}) — status ${res.data.status}`);
      setTopupOpen(false);
      setTopupAmount('');
      await loadAll();
    } catch (e) {
      // P0 (Anita Currie / Dallas) — backend now returns structured
      // detail bodies (`{code, message, hint, retryable}`) so admins see
      // the real Stripe reason instead of a blank "Topup failed" toast.
      const d = e.response?.data?.detail;
      const status = e.response?.status;
      if (d && d.code === 'mandate_required') {
        // 2026-08-31 — one-time in-app bank authorization flow.
        setMandateNeeded(true);
      }
      const msg = typeof d === 'string'
        ? d
        : (d && (d.message || d.hint || d.code)) || e.message || 'Topup failed';
      const httpTxt = status ? ` (HTTP ${status})` : '';
      toast.error(`${msg}${httpTxt}`, {
        description: (d && d.hint && d.hint !== msg) ? d.hint : undefined,
        duration: 8000,
      });
    }
    finally { setTopupBusy(false); }
  };

  // 2026-08-31 — one-time bank authorization (ACH mandate) so topups
  // can debit the linked bank account in-app.
  const authorizeBank = async () => {
    setMandateBusy(true);
    setMandateAction(null);
    try {
      const res = await api.post('/admin/stripe/setup-bank-mandate', {});
      if (res.data.status === 'succeeded') {
        toast.success('Bank account authorized', {
          description: 'The ACH mandate is active — retrying your top-up now.',
        });
        setMandateNeeded(false);
        await submitTopup();
      } else if (res.data.status === 'requires_action') {
        setMandateAction(res.data.next_action || {});
        const url = res.data.next_action?.hosted_verification_url;
        if (url) window.open(url, '_blank', 'noopener');
        toast.info('One-time verification needed', {
          description: url
            ? 'Complete the bank verification in the tab that just opened, then retry the top-up.'
            : 'Stripe requires additional verification. Check the notification email from Stripe, then retry.',
          duration: 10000,
        });
      } else {
        toast.info(`Authorization status: ${res.data.status}`, {
          description: 'Retry the top-up once Stripe finishes processing.',
        });
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && (d.message || d.code)) || 'Bank authorization failed', { duration: 8000 });
    } finally { setMandateBusy(false); }
  };

  // Iter-328 Phase 2 — send onboarding reminders to all not-payout-ready talent
  const sendAllReminders = async () => {
    const ids = (readiness.talents_not_payout_ready || []).map(t => t.talent_id);
    if (ids.length === 0) return;
    if (!window.confirm(`Send onboarding reminder to ${ids.length} talent?`)) return;
    setReminderBusy(true);
    try {
      const res = await api.post('/admin/staff-payments/send-onboarding-reminder', { talent_ids: ids });
      toast.success(`Reminder sent to ${res.data.count} talent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder failed');
    } finally { setReminderBusy(false); }
  };
  const sendOneReminder = async (talentId) => {
    setReminderBusy(true);
    try {
      await api.post('/admin/staff-payments/send-onboarding-reminder', { talent_ids: [talentId] });
      toast.success('Reminder sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder failed');
    } finally { setReminderBusy(false); }
  };

  const summary = useMemo(() => {
    const totals = { count: batches.length, gross: 0, transferred: 0 };
    batches.forEach((b) => {
      totals.gross += Number(b.total_gross || 0);
      totals.transferred += Number(b.total_transferred || 0);
    });
    return totals;
  }, [batches]);

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="admin-staff-payments">
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="staff-pay-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Contractor Payments</h1>
            <p className="text-xs text-slate-500">Native Stripe Connect contractor payouts — funded by client invoices</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Critical Pay priority queue — prioritized 7-business-day payouts
            surface at the TOP of the accounting workflow. */}
        <CriticalPayQueueCard />

        {/* Sprint T2P — full booking → payment lifecycle. Sits above
            the batch payout surface so admins see BOTH the granular
            per-booking view and the Stripe Connect batch view without
            switching pages. */}
        <AdminStaffPaymentsLifecycle />

        {/* Balance widget */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="platform-balance-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm text-slate-600 font-medium flex items-center gap-2">
                <Wallet className="w-4 h-4" /> Available Stripe balance
              </CardTitle>
              <Button size="icon" variant="ghost" onClick={loadAll} data-testid="refresh-balance-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-teal-700" data-testid="available-balance">{fmt(balance.available_usd)}</div>
              <div className="text-xs text-slate-500 mt-1">Pending: {fmt(balance.pending_usd)}</div>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => setTopupOpen(true)}
                data-testid="add-funds-btn"
              >
                <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add funds
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 font-medium">Batches in queue</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="batches-count">{summary.count}</div>
              <div className="text-xs text-slate-500 mt-1">Total gross {fmt(summary.gross)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 font-medium">Transferred (all-time in view)</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-700">{fmt(summary.transferred)}</div>
              <div className="text-xs text-slate-500 mt-1">Recorded via Stripe Transfers</div>
            </CardContent>
          </Card>
        </div>

        {/* Iter-328 Phase 2 — Blockers panel */}
        {(readiness.talents_not_payout_ready || []).length > 0 && (
          <Card className="border-rose-200 bg-rose-50/40" data-testid="blockers-panel">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base text-rose-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {readiness.talents_not_payout_ready.length} talent not payout-ready
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-100"
                onClick={sendAllReminders}
                disabled={reminderBusy}
                data-testid="send-all-reminders-btn"
              >
                {reminderBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send onboarding reminders to all
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {readiness.talents_not_payout_ready.slice(0, 12).map((t) => (
                  <div key={t.talent_id} className="px-4 py-2 flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{t.talent_label}</div>
                      <div className="text-xs text-slate-500 truncate">{t.email} · {t.reason.replace(/_/g, ' ')}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => sendOneReminder(t.talent_id)}
                      disabled={reminderBusy}
                      data-testid={`remind-${t.talent_id}`}
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">Filter:</span>
          {['', 'payment_ready', 'payment_approved', 'stripe_funding_pending', 'transferred', 'paid', 'failed'].map((s) => (
            <Button
              key={s || 'all'}
              size="sm"
              variant={statusFilter === s ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s || 'all'}`}
              className="text-xs"
            >
              {s ? STATUS_META[s]?.text : 'All'}
            </Button>
          ))}
        </div>

        {/* Batch list */}
        <Card>
          <CardHeader><CardTitle className="text-base">Payment batches</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex items-center justify-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
            ) : batches.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">
                No batches yet. Go to an invoice → Mark Funding Received → Build Payment Batch.
              </div>
            ) : (
              <div className="divide-y">
                {batches.map((b) => {
                  const m = STATUS_META[b.status] || STATUS_META.payment_ready;
                  return (
                    <button
                      key={b.id}
                      onClick={() => openDetail(b.id)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 flex flex-wrap items-center gap-3"
                      data-testid={`batch-row-${b.id}`}
                    >
                      <div className="flex-1 min-w-[200px]">
                        <div className="font-medium">{b.event_title || '—'}</div>
                        <div className="text-xs text-slate-500">
                          {b.invoice_number} · {b.shift_date || '—'} · {b.transfer_count || 0} transfers
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{fmt(b.total_gross)}</div>
                        <div className="text-xs text-slate-500">Transferred: {fmt(b.total_transferred)}</div>
                      </div>
                      <Badge className={`border ${m.cls}`}>{m.text}</Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Batch Detail Dialog ─── */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="batch-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-teal-600" /> Payment batch</DialogTitle>
            <DialogDescription className="sr-only">Payment batch detail</DialogDescription>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="py-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-slate-500">Invoice</div>
                  <div className="font-medium">{detail.invoice_number}</div>
                </div>
                <div>
                  <div className="text-slate-500">Status</div>
                  <Badge className={`border ${(STATUS_META[detail.status] || STATUS_META.payment_ready).cls}`} data-testid="batch-detail-status">
                    {(STATUS_META[detail.status] || {}).text || detail.status}
                  </Badge>
                </div>
                <div>
                  <div className="text-slate-500">Event / Date</div>
                  <div>{detail.event_title || '—'} · {detail.shift_date || '—'}</div>
                </div>
                <div>
                  <div className="text-slate-500">Totals</div>
                  <div>{fmt(detail.total_gross)} gross · {fmt(detail.total_transferred)} transferred</div>
                </div>
              </div>

              {approveResult && !approveResult.can_execute && (
                <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">{approveResult.message}</div>
                    <div className="mt-1">
                      <Button size="sm" variant="outline" onClick={() => setTopupOpen(true)} data-testid="approve-shortfall-topup-btn">
                        Add funds
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="text-sm font-semibold mb-2">Talent lines ({detail.lines.length})</div>
                <div className="rounded border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600">
                      <tr>
                        <th className="text-left p-2">Talent</th>
                        <th className="text-right p-2">Hours</th>
                        <th className="text-right p-2">Amount</th>
                        <th className="text-center p-2">Status</th>
                        <th className="text-right p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lines.map((l) => (
                        <tr key={l.payout_id} className="border-t" data-testid={`batch-line-${l.payout_id}`}>
                          <td className="p-2">
                            <div className="font-medium">{l.talent_label}</div>
                            {!l.payouts_ready && (
                              <div className="text-xs text-red-600">Stripe not ready</div>
                            )}
                            {l.held_at && (
                              <div className="text-xs text-amber-700">Held: {l.hold_reason}</div>
                            )}
                            {l.failed_reason && (
                              <div className="text-xs text-red-600">Fail: {l.failed_reason.slice(0, 60)}</div>
                            )}
                          </td>
                          <td className="p-2 text-right">{Number(l.hours).toFixed(2)}</td>
                          <td className="p-2 text-right font-semibold">{fmt(l.amount)}</td>
                          <td className="p-2 text-center">
                            <Badge className="border bg-slate-100 text-slate-700 border-slate-200 text-[10px]">
                              {l.status}
                            </Badge>
                          </td>
                          <td className="p-2 text-right space-x-1">
                            {l.status === 'pending' && !l.held_at && (
                              <Button size="icon" variant="ghost" onClick={() => holdLine(l.payout_id)} title="Hold" data-testid={`hold-${l.payout_id}`}>
                                <Pause className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {l.held_at && (
                              <Button size="icon" variant="ghost" onClick={() => releaseLine(l.payout_id)} title="Release" data-testid={`release-${l.payout_id}`}>
                                <Play className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {l.status === 'failed' && (
                              <Button size="icon" variant="ghost" onClick={() => retryLine(l.payout_id)} title="Retry" data-testid={`retry-${l.payout_id}`}>
                                <RotateCcw className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {l.status === 'completed' && (
                              <Button size="icon" variant="ghost" onClick={() => instantPayout(l.payout_id)} title="Send Instant Payout (requires talent debit card)" data-testid={`instant-${l.payout_id}`}>
                                <Zap className="w-3.5 h-3.5 text-amber-500" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {detail.status === 'payment_ready' && (
                  <Button onClick={approve} disabled={actionBusy} data-testid="approve-batch-btn">
                    {actionBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Approve batch
                  </Button>
                )}
                {(detail.status === 'payment_approved' || detail.status === 'stripe_funding_pending') && (
                  <Button onClick={execute} disabled={actionBusy} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="execute-batch-btn">
                    {actionBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Execute payout ({fmt(detail.total_gross)})
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Topup Dialog ─── */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PlusCircle className="w-5 h-5 text-teal-600" /> Add funds to Stripe</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Pulls funds via ACH from your linked business bank account. Settlement is typically 4–5 US business days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="topup-amt">Amount (USD)</Label>
              <Input
                id="topup-amt"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="0.00"
                data-testid="topup-amount"
              />
            </div>
            <div>
              <Label htmlFor="topup-desc">Description</Label>
              <Textarea id="topup-desc" rows={2} value={topupDesc} onChange={(e) => setTopupDesc(e.target.value)} data-testid="topup-description" />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
              A verified business bank account must already be linked in Stripe Dashboard → Settings → Bank accounts before this will settle.
            </div>
            <div className="border border-slate-200 rounded-lg p-3 space-y-1.5" data-testid="alert-buffer-box">
              <Label htmlFor="alert-buffer" className="text-xs font-semibold text-slate-700">
                Low-balance alert buffer (USD)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="alert-buffer"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="50"
                  value={alertBuffer}
                  onChange={(e) => setAlertBuffer(e.target.value)}
                  placeholder="0"
                  className="h-9"
                  data-testid="alert-buffer-input"
                />
                <Button size="sm" variant="outline" onClick={saveBuffer} disabled={bufferSaving} data-testid="alert-buffer-save">
                  {bufferSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                Daily email warning fires when Stripe balance drops below upcoming payouts + this buffer.
              </p>
            </div>
            {mandateNeeded && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="mandate-required-box">
                <p className="text-xs font-semibold text-amber-900">
                  One-time bank authorization required
                </p>
                <p className="text-xs text-amber-800">
                  Stripe needs an ACH mandate on your linked bank account before top-ups can
                  debit it. This is a one-time step — once authorized, all future Add Funds
                  work directly in-app.
                </p>
                {mandateAction && (
                  <p className="text-xs text-amber-800 font-medium">
                    {mandateAction.hosted_verification_url ? (
                      <>Verification pending — <a href={mandateAction.hosted_verification_url} target="_blank" rel="noopener noreferrer" className="underline">complete it here</a>, then retry the top-up.</>
                    ) : (
                      'Verification pending with Stripe — complete it, then retry the top-up.'
                    )}
                  </p>
                )}
                <Button
                  size="sm"
                  onClick={authorizeBank}
                  disabled={mandateBusy}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="authorize-bank-btn"
                >
                  {mandateBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Authorize bank account
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)} disabled={topupBusy}>Cancel</Button>
            <Button onClick={submitTopup} disabled={topupBusy || !topupAmount} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="topup-submit-btn">
              {topupBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Initiate ACH topup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
